type Point = { x: number; y: number };

type TriangleShapeProps = {
  fill?: string;
  stroke?: string;
  strokeWidth?: number;
  width?: number;
  height?: number;
  borderRadius?: number;
  lockAspectRatio?: boolean;
} & React.SVGProps<SVGSVGElement>;

function getLineIntersection(p1: Point, p2: Point, p3: Point, p4: Point): Point {
  const a1 = p2.y - p1.y;
  const b1 = p1.x - p2.x;
  const c1 = a1 * p1.x + b1 * p1.y;

  const a2 = p4.y - p3.y;
  const b2 = p3.x - p4.x;
  const c2 = a2 * p3.x + b2 * p3.y;

  const det = a1 * b2 - a2 * b1;

  if (Math.abs(det) < 1e-10) return p2;

  return {
    x: (b2 * c1 - b1 * c2) / det,
    y: (a1 * c2 - a2 * c1) / det,
  };
}

function getInsetTrianglePoints(points: Point[], inset: number): Point[] {
  const offsetEdges: { p1: Point; p2: Point }[] = [];

  const len = points.length;
  for (let i = 0; i < len; i++) {
    const a = points[i];
    const b = points[(i + 1) % len];

    const dx = b.x - a.x;
    const dy = b.y - a.y;
    const length = Math.hypot(dx, dy);

    const nx = -dy / length;
    const ny = dx / length;

    const offsetA = { x: a.x + nx * inset, y: a.y + ny * inset };
    const offsetB = { x: b.x + nx * inset, y: b.y + ny * inset };

    offsetEdges.push({ p1: offsetA, p2: offsetB });
  }

  const innerPoints: Point[] = [];
  for (let i = 0; i < len; i++) {
    const line1 = offsetEdges[i];
    const line2 = offsetEdges[(i + 1) % len];
    innerPoints.push(getLineIntersection(line1.p1, line1.p2, line2.p1, line2.p2));
  }

  return innerPoints;
}

function pointsToPath(points: Point[]): string {
  return `M ${points[0].x} ${points[0].y} L ${points[1].x} ${points[1].y} L ${points[2].x} ${points[2].y} Z`;
}

const TriangleShapeComponent: React.FC<TriangleShapeProps> = ({
  fill = 'none',
  stroke = 'none',
  strokeWidth = 0,
  width = 200,
  height = 200,
  borderRadius: _borderRadius,
  lockAspectRatio: _lockAspectRatio,
  ...rest
}) => {
  const actualStrokeWidth = strokeWidth * 10;
  const rawOuterPoints: Point[] = [
    { x: width / 2, y: 0 },
    { x: width, y: height },
    { x: 0, y: height },
  ];

  const outerPoints = getInsetTrianglePoints(rawOuterPoints, actualStrokeWidth / 2);
  const innerPoints = getInsetTrianglePoints(outerPoints, actualStrokeWidth / 2);

  return (
    <svg
      width="100%"
      height="100%"
      viewBox={`0 0 ${width} ${height}`}
      preserveAspectRatio="xMidYMid meet"
      xmlns="http://www.w3.org/2000/svg"
      {...rest}
    >
      <path d={pointsToPath(outerPoints)} stroke={stroke} strokeWidth={actualStrokeWidth} fill="none" />
      <path d={pointsToPath(innerPoints)} fill={fill} stroke="none" />
    </svg>
  );
};
export default TriangleShapeComponent;
